import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { BsCashStack, BsClock } from 'react-icons/bs';
import { LuTicketPercent } from 'react-icons/lu';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { Button, Tab } from '@/components';
import { toast } from '@/components/toastStore';
import { useInfiniteScrollSentinel } from '@/hooks/useInfiniteScrollSentinel';
import { PackageType, type Booking, type CustomerPackage } from '@/models';
import { BookingHistoryDetailBottomSheet } from '@/pages/components/bottomsheets';
import ProfileAvatar from '@/pages/components/ProfileAvatar';
import ReorderBottomSheet from '@/pages/components/ReorderBottomSheet';
import BookingHistoryItem from '@/pages/profile/components/BookingHistoryItem';
import BookingHistorySkeletonList from '@/pages/profile/components/BookingHistorySkeletonList';
import ProfileStickyFooter from '@/pages/profile/components/ProfileStickyFooter';
import PackageDetailsPage from '@/pages/profile/PackageDetailsPage';
import { ProfileService } from '@/services';
import { getLiffProfile } from '@/services/liffService';
import { useStoredProfile } from '@/services/profileService';

type ProfileTab = 'bookingHistory' | 'packages';

interface BookingHistoryEmptyStateProps {
  onBookNowClick: () => void;
}

const BookingHistoryEmptyState: FC<BookingHistoryEmptyStateProps> = ({
  onBookNowClick,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 pb-10">
      <p className="text-secondary-text max-w-[328px] text-center text-base leading-6">
        {t('profile.home.emptyState.messageLine1')}
        <br />
        {t('profile.home.emptyState.messageLine2')}
      </p>

      <img
        src="/illustrations/no-booking-history.png"
        alt={t('profile.home.emptyState.illustrationAlt')}
        className="h-[160px] w-[200px] object-contain"
      />

      <Button
        type="button"
        variant="primary"
        size="lg"
        className="w-[200px]"
        onClick={onBookNowClick}
      >
        {t('profile.home.actions.bookNow')}
      </Button>
    </div>
  );
};

const PackagesEmptyState: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 pb-10">
      <p className="text-secondary-text max-w-[328px] text-center text-base leading-6">
        {t('profile.home.packages.emptyState.messageLine1')}
        <br />
        {t('profile.home.packages.emptyState.messageLine2')}
      </p>

      <img
        src="/illustrations/no-package-profile.png"
        alt={t('profile.home.packages.emptyState.illustrationAlt')}
        className="h-[200px] w-[200px] object-contain"
        draggable={false}
      />
    </div>
  );
};

interface PackageCardProps {
  customerPackage: CustomerPackage;
  onClick: () => void;
}

interface PackageCardSkeletonListProps {
  count?: number;
}

const PackageCardSkeletonItem: FC = () => {
  return (
    <div
      className="border-border-inactive rounded-lg border"
      aria-hidden="true"
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-2 p-2">
          <div className="bg-divider h-6 w-8 animate-pulse rounded" />
          <div className="bg-divider h-5 w-40 max-w-full animate-pulse rounded" />
        </div>

        <div className="border-divider border-t" />

        <div className="flex gap-1 px-2">
          <div className="flex flex-1 flex-col gap-1 py-1">
            <div className="bg-divider h-4 w-20 animate-pulse rounded" />
            <div className="bg-divider h-5 w-24 animate-pulse rounded" />
          </div>

          <div className="bg-divider w-px self-stretch" aria-hidden />

          <div className="flex flex-1 flex-col gap-1 py-1">
            <div className="bg-divider h-4 w-16 animate-pulse rounded" />
            <div className="bg-divider h-5 w-28 animate-pulse rounded" />
          </div>
        </div>
      </div>
    </div>
  );
};

const PackageCardSkeletonList: FC<PackageCardSkeletonListProps> = ({
  count = 3,
}) => {
  return (
    <div className="flex flex-col gap-3" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <PackageCardSkeletonItem key={index} />
      ))}
    </div>
  );
};

const PackageCard: FC<PackageCardProps> = ({ customerPackage, onClick }) => {
  const { t } = useTranslation();

  const expiryLabel = dayjs(customerPackage.expiryDate).format('DD/MM/YYYY');
  const remainsText = (() => {
    const remaining = customerPackage.remainingValue.toLocaleString();
    const total = customerPackage.totalValue.toLocaleString();

    switch (customerPackage.type) {
      case PackageType.Times:
        return `${remaining}/${total} ${t('package.unit.times')}`;
      case PackageType.Minutes:
        return `${remaining} ${t('package.unit.minutes')}`;
      case PackageType.CashWallet:
        return `${remaining} ${t('package.unit.baht')}`;
      case PackageType.Percent:
        return remaining;
      default:
        return `${remaining}/${total}`;
    }
  })();

  const meta = (() => {
    switch (customerPackage.type) {
      case PackageType.CashWallet:
        return {
          iconBgClass: 'bg-wallet-bg',
          iconColorClass: 'text-accent',
          icon: <BsCashStack className="h-3 w-3" />,
          remainsLabel: t('profile.home.packages.card.remaining'),
          expiryLabel: t('profile.home.packages.card.expire'),
        };
      case PackageType.Times:
        return {
          iconBgClass: 'bg-times-bg',
          iconColorClass: '',
          icon: (
            <img
              src="/icons/treatment_time.png"
              className="h-3 w-3 object-contain"
            />
          ),
          remainsLabel: t('profile.home.packages.card.remaining'),
          expiryLabel: t('profile.home.packages.card.expire'),
        };
      case PackageType.Minutes:
        return {
          iconBgClass: 'bg-minutes-bg',
          iconColorClass: 'text-minutes-text',
          icon: <BsClock className="h-3 w-3" />,
          remainsLabel: t('profile.home.packages.card.remaining'),
          expiryLabel: t('profile.home.packages.card.expire'),
        };
      case PackageType.Percent:
        return {
          iconBgClass: 'bg-discount-bg',
          iconColorClass: 'text-discount-text',
          icon: <LuTicketPercent className="h-3 w-3" />,
          remainsLabel: t('profile.home.packages.card.remaining'),
          expiryLabel: t('profile.home.packages.card.expire'),
        };
      default:
        return null;
    }
  })();

  if (!meta) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="border-border-inactive cursor-pointer rounded-lg border"
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-2 p-2">
          <div
            className={`${meta.iconBgClass} inline-flex h-6 items-center justify-center rounded px-[3px]`}
            aria-hidden
          >
            <div className="inline-flex items-center rounded-full bg-white p-[3px]">
              <span
                className={`${meta.iconColorClass} inline-flex h-3 w-3 items-center justify-center`}
              >
                {meta.icon}
              </span>
            </div>
          </div>

          <p className="text-primary-text text-base leading-6 font-medium">
            {customerPackage.name}
          </p>
        </div>

        <div className="border-divider border-t" />

        <div className="flex gap-1 px-2">
          <div className="flex flex-1 flex-col gap-0.5 py-1">
            <p className="text-text-disabled text-sm leading-5 font-semibold">
              {meta.remainsLabel}
            </p>
            <p className="text-primary-text text-base leading-6">
              {remainsText}
            </p>
          </div>

          <div className="bg-divider w-px self-stretch" aria-hidden />

          <div className="flex flex-1 flex-col gap-0.5 py-1">
            <p className="text-text-disabled text-sm leading-5 font-semibold">
              {meta.expiryLabel}
            </p>
            <p className="text-primary-text text-base leading-6">
              {expiryLabel}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfileHomePage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleBookNowClick = () => {
    void navigate('/booking');
  };

  const storedProfile = useStoredProfile();
  const displayName = storedProfile?.name || '—';

  const [selectedTab, setSelectedTab] = useState<ProfileTab>('bookingHistory');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [reorderBooking, setReorderBooking] = useState<Booking | null>(null);
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
  const bookingHistoryErrorToastShownRef = useRef(false);
  const loggedInToastShownRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const liffProfile = await getLiffProfile();
      if (!cancelled && liffProfile?.pictureUrl) {
        setAvatarSrc(liffProfile.pictureUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const navState = (location.state ?? {}) as {
      showLoggedInToast?: boolean;
      phoneNumber?: string;
    };

    if (!navState.showLoggedInToast) {
      loggedInToastShownRef.current = false;
      return;
    }

    if (loggedInToastShownRef.current) return;
    loggedInToastShownRef.current = true;

    toast.success(t('profile.home.toast.loggedIn'));

    navigate('/profile/home', {
      replace: true,
      state: {
        phoneNumber: navState.phoneNumber,
      },
    });
  }, [location.state, navigate, t]);

  const bookingsQuery = useInfiniteQuery({
    queryKey: ['profile', 'myBookings'],
    enabled: selectedTab === 'bookingHistory',
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      return ProfileService.getMyBookings({
        limit: 10,
        page: Number(pageParam),
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      if (nextPage > lastPage.totalPage) return undefined;
      return nextPage;
    },
  });

  const bookingHistory = useMemo((): Booking[] => {
    if (!bookingsQuery.data) return [];
    return bookingsQuery.data.pages.flatMap((p) => p.items);
  }, [bookingsQuery.data]);

  const {
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: bookingsLoading,
  } = bookingsQuery;

  const bookingSentinelRef = useInfiniteScrollSentinel({
    enabled: selectedTab === 'bookingHistory',
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  });

  useEffect(() => {
    if (!bookingsQuery.isError) {
      bookingHistoryErrorToastShownRef.current = false;
      return;
    }

    if (bookingHistoryErrorToastShownRef.current) return;
    bookingHistoryErrorToastShownRef.current = true;

    console.error('Failed to load booking history', bookingsQuery.error);
    toast.error(t('profile.home.errors.loadBookingsFailed'));
  }, [bookingsQuery.error, bookingsQuery.isError, t]);

  const packagesQuery = useQuery({
    queryKey: ['profile', 'myCustomerPackages'],
    enabled: selectedTab === 'packages',
    queryFn: async () => {
      const response = await ProfileService.getMyCustomerPackages();
      return response.items.filter(
        (p) =>
          p.type === PackageType.CashWallet ||
          p.type === PackageType.Times ||
          p.type === PackageType.Minutes ||
          p.type === PackageType.Percent,
      );
    },
  });

  const packagesErrorToastShownRef = useRef(false);

  useEffect(() => {
    if (selectedTab !== 'packages') return;

    if (!packagesQuery.isError) {
      packagesErrorToastShownRef.current = false;
      return;
    }

    if (packagesErrorToastShownRef.current) return;
    packagesErrorToastShownRef.current = true;

    console.error('Failed to load packages', packagesQuery.error);
    toast.error(t('package.failedToLoad'));
  }, [packagesQuery.error, packagesQuery.isError, selectedTab, t]);

  const customerPackages = packagesQuery.data ?? [];
  const packagesLoading = packagesQuery.isLoading;

  const tabs = useMemo((): { value: ProfileTab; label: string }[] => {
    return [
      {
        value: 'bookingHistory',
        label: t('profile.home.tabs.bookingHistory'),
      },
      { value: 'packages', label: t('profile.home.tabs.packages') },
    ];
  }, [t]);

  const selectedPackageId = useMemo(() => {
    const raw = searchParams.get('packageId');
    if (!raw) return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  }, [searchParams]);

  const openPackageDetails = (id: number) => {
    setSelectedTab('packages');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('packageId', String(id));
      return next;
    });
  };

  const closePackageDetails = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('packageId');
      return next;
    });
  };

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col pb-16">
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col items-center gap-2 pt-2">
          <ProfileAvatar src={avatarSrc} size={60} />
          <p className="text-primary-text text-lg leading-7">{displayName}</p>
        </div>

        <div className="pt-4">
          <Tab
            tabs={tabs}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
          />
        </div>

        <div className="flex flex-1 flex-col pt-4">
          {selectedTab === 'bookingHistory' ? (
            bookingsLoading && bookingHistory.length === 0 ? (
              <BookingHistorySkeletonList />
            ) : bookingHistory.length > 0 ? (
              <div className="flex flex-col gap-2">
                {bookingHistory.map((booking, index) => (
                  <BookingHistoryItem
                    key={`${booking.availabilityId}-${index}`}
                    booking={booking}
                    onReorderClick={() => {
                      setReorderBooking(booking);
                    }}
                    onClick={() => {
                      setSelectedBooking(booking);
                    }}
                  />
                ))}

                <div ref={bookingSentinelRef} className="h-0.5" />
                {isFetchingNextPage ? (
                  <div className="pt-2">
                    <BookingHistorySkeletonList count={1} />
                  </div>
                ) : null}
              </div>
            ) : (
              <BookingHistoryEmptyState onBookNowClick={handleBookNowClick} />
            )
          ) : selectedTab === 'packages' ? (
            packagesLoading ? (
              <PackageCardSkeletonList />
            ) : customerPackages.length > 0 ? (
              <div className="flex flex-col gap-3">
                {customerPackages.map((pkg) => (
                  <PackageCard
                    key={pkg.id}
                    customerPackage={pkg}
                    onClick={() => {
                      openPackageDetails(pkg.id);
                    }}
                  />
                ))}
              </div>
            ) : (
              <PackagesEmptyState />
            )
          ) : null}
        </div>
      </div>

      <BookingHistoryDetailBottomSheet
        isOpen={selectedBooking !== null}
        onClose={() => {
          setSelectedBooking(null);
        }}
        booking={selectedBooking}
        onReorderClick={(booking: Booking) => {
          setSelectedBooking(null);
          setReorderBooking(booking);
        }}
      />

      <ReorderBottomSheet
        isOpen={reorderBooking !== null}
        onClose={() => {
          setReorderBooking(null);
        }}
        booking={reorderBooking}
      />

      <PackageDetailsPage
        isOpen={selectedPackageId !== null}
        onClose={closePackageDetails}
        packageId={selectedPackageId}
      />

      {selectedPackageId === null ? <ProfileStickyFooter /> : null}
    </div>
  );
};

export default ProfileHomePage;
